import { Box, Flex } from "rebass/styled-components";
import { HiMenu } from "react-icons/hi";
import styled from "styled-components";
import { Link, useLocation } from "react-router-dom";
import { useRecoilState } from "recoil";

import { ReactComponent as HeaderLogo } from "../../../assets/images/logo/headerLogo.svg";
import { SidebarExpandedState } from "../../State/sidebar";
import { pathExcludesNavigation } from "../../Utilities/Navigation/utils";
import { useIsDesktopApp } from "../../Hooks/useIsDesktopApp";
import { useIsMobileApp } from "../../Hooks/useIsMobileApp";
import { AuthenticatedSection } from "./AuthenticatedSection";
import { UserSearch } from "../UserSearch";
import { SiteNav } from "../SiteNav";
import { useNewNav } from "../../Hooks/useNewNav";
import {
  PAGE_GUTTER,
  PAGE_MAX_WIDTH,
} from "MVVM/Views/LandingPages/styledComponents";

const SiteHeader = ({ standalone }: { standalone?: boolean }) => {
  const [expanded, setExpanded] = useRecoilState(SidebarExpandedState);
  const { pathname: path } = useLocation();
  const isDesktopView = useIsDesktopApp();
  const isMobileApp = useIsMobileApp();
  const { newNav } = useNewNav();
  // Opt-in nav redesign, landing pages included: SiteNav wears the same
  // standalone frame so it drops straight into their layout.
  if (newNav) return <SiteNav standalone={standalone} />;

  if (standalone)
    return (
      <SiteHeaderContainer $standalone>
        <StandaloneWrapper>
          <Link to="/">
            <StyledHeaderLogo />
          </Link>
          <Box ml="auto">
            <AuthenticatedSection />
          </Box>
        </StandaloneWrapper>
      </SiteHeaderContainer>
    );

  if (
    pathExcludesNavigation(path) ||
    isDesktopView.isDesktop ||
    isMobileApp.isMobileApp
  )
    return <></>;

  return (
    <SiteHeaderContainer>
      <LeftGroup>
        <Box onClick={() => setExpanded(!expanded)}>
          <StyledHiMenu />
        </Box>
        <Link to="/">
          <StyledHeaderLogo />
        </Link>
        <SearchSlot>
          <UserSearch />
        </SearchSlot>
      </LeftGroup>
      {/* Explicit px, not a space-scale index: this rendered at 32px back when
          the header used the themeless emotion rebass build. */}
      <Box pr={[0, 0, "32px"]}>
        <AuthenticatedSection />
      </Box>
    </SiteHeaderContainer>
  );
};

// Same frame as the landing pages' Section, so the logo and auth buttons sit on
// the same edge as the content below them.
const StandaloneWrapper = styled(Flex)`
  align-items: center;
  justify-content: space-between;
  margin: 0 auto;
  max-width: ${PAGE_MAX_WIDTH};
  padding: 0 ${PAGE_GUTTER};
  width: 100%;
`;

const LeftGroup = styled(Flex)`
  align-items: center;
  flex: 1;
  gap: 36px;
  min-width: 0;
`;

const SearchSlot = styled(Box)`
  flex: 1;
  max-width: 560px;
  min-width: 0;
`;

const StyledHeaderLogo = styled(HeaderLogo)`
  height: 26px;
  width: auto;

  @media (max-width: ${(props) => props.theme.breaks.narrow}) {
    height: 22px;
  }
`;

const SiteHeaderContainer = styled(Flex)<{ $standalone?: boolean }>`
  align-items: center;
  background: ${({ theme }) => theme.colors.navy600};
  box-shadow: 0px 2px 8px rgba(0, 0, 0, 0.3);
  justify-content: space-between;
  /* Standalone owns its own gutter via StandaloneWrapper, so no outer offset. */
  padding: ${({ $standalone }) => ($standalone ? "18px 0" : "18px 28px")};
  position: relative;
  z-index: 101;
`;

const StyledHiMenu = styled(HiMenu)`
  color: ${({ theme }) => theme.colors.chalk};
  cursor: pointer;
  height: 24px;
  width: 24px;

  :hover {
    color: ${({ theme }) => theme.colors.envy};
  }
`;

export { SiteHeader };
